import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  User,
  ShoppingBag,
  MapPin,
  Settings,
  Clock,
  Repeat,
  Eye,
  ArrowLeft,
  Phone,
  Mail,
  LogOut,
  Star,
  Info,
} from "lucide-react";
import { Footer } from "../components/Footer";
import { SimpleRatingModal } from "../components/SimpleRatingModal";
import { useApp } from "../App";
import { getAllOrdersByUserId } from "@/services/menuService";
import { CartItem, MenuItem, Order } from "@/types";
import { formatCanadianPhoneNumber } from "@/services/utlits";

export function ProfilePage() {
  const navigate = useNavigate();
  const { restaurant, addToCart } = useApp();
  const { user, logout } = useApp();
  const [activeTab, setActiveTab] = useState("orders");
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [isOrderDetailsOpen, setIsOrderDetailsOpen] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [orderToRate, setOrderToRate] = useState<string>("");
  const [openTaxToolTip, setOpenTaxToolTip] = useState(false);

  const [orders, setOrders] = useState<Order[]>([]);

  const getStatusColor = (status: Order["status"]) => {
    switch (status.toLowerCase()) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "pending":
      case "preparing":
        return "bg-yellow-100 text-yellow-800";
      case "out-for-delivery":
      case "out for delivery":
        return "bg-blue-100 text-blue-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusText = (status: Order["status"]) => {
    switch (status.toLowerCase()) {
      case "pending":
        return "Pending";
      case "preparing":
        return "Preparing";
      case "delivered":
        return "Delivered";
      case "out-for-delivery":
      case "out for delivery":
        return "Out for Delivery";
      case "cancelled":
        return "Cancelled";
      default:
        return "Unknown";
    }
  };

  const getAllUserOrders = async () => {
    const response = await getAllOrdersByUserId();

    if (response && Array.isArray(response)) {
      setOrders(response);
    } else {
      console.error("Orders failed:", response.error);
    }
  };
  useEffect(() => {
    getAllUserOrders();
  }, []);
  const handleViewDetails = (order: Order) => {
    setSelectedOrder(order);
    setIsOrderDetailsOpen(true);
  };

  const handleRateOrder = (orderId: string) => {
    setOrderToRate(orderId);
    setIsRatingModalOpen(true);
  };

  const handleRatingSubmit = (ratingData: {
    orderId: string;
    rating: number;
    comment: string;
  }) => {
    // Update the order to mark it as rated
    // setOrders((prevOrders) =>
    //   prevOrders.map((order) =>
    //     order.id === ratingData.orderId ? { ...order, hasRating: true } : order
    //   )
    // );

    // Show success message
    alert(
      `Thank you for rating order ${ratingData.orderId}! Your ${ratingData.rating}-star rating has been submitted.`
    );
  };
  const repeatOrder = (order: Order) => {
    order.items.forEach((item) => {
      const cartItem: CartItem = {
        id: item.id,
        name: item.name,
        description: "", // fallback if not stored in order
        categoryId: "",
        category: "", // fallback
        basePrice: item.price,
        discountPrice: item.price, // if no discount info
        imageUrl: item.image,
        quantity: item.quantity,
        totalPrice: item.price * item.quantity,
        customizations: item.customizations.map((c) => ({
          groupId: c.groupId,
          groupName: c.groupName,
          optionId: c.optionId,
          optionName: c.optionName,
          price: c.price,
          quantity: c.quantity ?? 1, // fallback if undefined
        })),
        specialInstructions: "", // or use actual past instructions if available
      };
      addToCart(cartItem);
    });
    navigate("/checkout");
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-6 sm:py-8 animate-fade-in-up">
        <div className="max-w-7xl mx-auto mobile-padding">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 sm:mb-8 space-y-4 sm:space-y-0 animate-slide-in-down">
            <div className="flex items-center space-x-4">
              <Link to="/">
                <Button
                  variant="ghost"
                  className="flex items-center space-x-2 transition-all duration-300 hover:-translate-x-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">Back to Menu</span>
                  <span className="sm:hidden">Back</span>
                </Button>
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 font-display">
                  My Account
                </h1>
                <p className="text-gray-600 text-sm sm:text-base">
                  Manage your profile and orders
                </p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="text-red-600 hover:text-red-700 transition-all duration-300 hover:scale-105"
            >
              <LogOut className="w-4 h-4 mr-2" />
              <span className="hidden sm:inline">Sign Out</span>
              <span className="sm:hidden">Logout</span>
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 sm:gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1 space-y-4 sm:space-y-6 animate-slide-in-left">
              {/* Profile Card */}
              <Card className="animate-zoom-in">
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-center space-x-4 mb-4 sm:mb-6">
                    <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-emerald-600 to-emerald-700 rounded-full flex items-center justify-center animate-pulse-slow">
                      <User className="w-6 h-6 sm:w-8 sm:h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                        {user.name}
                      </h3>
                      {user.role == "Guest" && (
                        <p className="text-gray-600 text-sm sm:text-base">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Total Orders</span>
                      <span className="font-semibold">{orders.length}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Member Since</span>
                      {user?.createdDate && (
                        <span className="font-semibold">
                          {new Date(user.createdDate).toLocaleDateString(
                            "en-US",
                            {
                              year: "numeric",
                              month: "short",
                            }
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Navigation */}
              <Card
                className="animate-zoom-in"
                style={{ animationDelay: "0.1s" }}
              >
                <CardContent className="p-3 sm:p-4">
                  <div className="space-y-2">
                    <Button
                      variant={activeTab === "orders" ? "default" : "ghost"}
                      className="w-full justify-start transition-all duration-300 hover:scale-105"
                      onClick={() => setActiveTab("orders")}
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      <span className="text-sm sm:text-base">
                        Order History
                      </span>
                    </Button>
                    {/* <Button
                      variant={activeTab === "addresses" ? "default" : "ghost"}
                      className="w-full justify-start transition-all duration-300 hover:scale-105"
                      onClick={() => setActiveTab("addresses")}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      <span className="text-sm sm:text-base">Addresses</span>
                    </Button> */}
                    <Button
                      variant={activeTab === "settings" ? "default" : "ghost"}
                      className="w-full justify-start transition-all duration-300 hover:scale-105"
                      onClick={() => setActiveTab("settings")}
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      <span className="text-sm sm:text-base">Settings</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3 animate-slide-in-right">
              {/* Order History */}
              {activeTab === "orders" && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 font-display">
                      Order History
                    </h2>
                    <Badge variant="secondary" className="w-fit">
                      {orders.length} orders
                    </Badge>
                  </div>
                  <div className="space-y-4">
                    {orders.map((order, index) => (
                      <Card
                        key={order?.orderId}
                        className="animate-fade-in-up transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      >
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-4 space-y-3 sm:space-y-0">
                            <div>
                              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3 mb-2">
                                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                                  Order #{order.orderId}
                                </h3>
                                <div className="flex items-center space-x-2">
                                  <Badge
                                    className={getStatusColor(order.status)}
                                  >
                                    {getStatusText(order.status)}
                                  </Badge>
                                  {order?.hasRating && (
                                    <Badge className="bg-yellow-100 text-yellow-800">
                                      <Star className="w-3 h-3 mr-1 fill-current" />
                                      Rated
                                    </Badge>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-gray-600">
                                <span className="flex items-center space-x-1">
                                  <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span>
                                    {new Date(
                                      order?.createdAt
                                    )?.toLocaleDateString()}
                                  </span>
                                </span>
                                {/* <span className="flex items-center space-x-1">
                                  <MapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                                  <span>{order?.restaurant}</span>
                                </span> */}
                              </div>
                            </div>
                            <div className="text-left sm:text-right">
                              <div className="text-xl sm:text-2xl font-bold text-emerald-600">
                                ${order?.totalAmount?.toFixed(2)}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-600">
                                {order.items.length} item
                                {order.items.length !== 1 ? "s" : ""}
                              </div>
                            </div>
                          </div>

                          <Separator className="my-4" />

                          <div className="space-y-2 mb-4">
                            {order?.items.map((item, index) => (
                              <div
                                key={index}
                                className="flex items-center justify-between text-xs sm:text-sm"
                              >
                                <span className="text-gray-900">
                                  {item?.quantity}x {item?.name}
                                </span>
                                <span className="font-medium text-gray-900">
                                  ${(item?.price * item?.quantity)?.toFixed(2)}
                                </span>
                              </div>
                            ))}
                          </div>

                          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                            <Button
                              size="lg"
                              variant="outline"
                              // className="flex-1 bg-white text-black shadow-md hover:shadow-lg hover:scale-105"
                              className="w-full sm:flex-1 bg-white text-black shadow-md hover:shadow-lg hover:scale-105 text-base py-4"
                              onClick={() => handleViewDetails(order)}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              View Details
                            </Button>

                            <Button
                              size="lg"
                              className="w-full sm:flex-1 bg-green-600 text-white hover:bg-green-700 transition-all duration-300 hover:scale-105 text-base py-4"
                              onClick={() => repeatOrder(order)}
                            >
                              <Repeat className="w-4 h-4 mr-2" />
                              Reorder
                            </Button>

                            {/* {order.status === "delivered" &&
                              !order.hasRating && (
                                <Button
                                  size="sm"
                                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white transition-all duration-300 hover:scale-105"
                                  onClick={() =>
                                    handleRateOrder(order?.orderId)
                                  }
                                >
                                  <Star className="w-4 h-4 mr-2" />
                                  Rate Order
                                </Button>
                              )} */}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Addresses */}
              {activeTab === "addresses" && (
                <div className="space-y-4 sm:space-y-6">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 font-display">
                      Saved Addresses
                    </h2>
                    <Button className="btn-primary w-full sm:w-auto">
                      Add New Address
                    </Button>
                  </div>
                  <Card className="animate-zoom-in">
                    <CardContent className="p-4 sm:p-6">
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                            Home
                          </h3>
                          <p className="text-gray-600 text-sm sm:text-base">
                            1275 Westwood Blvd
                          </p>
                          <p className="text-gray-600 text-sm sm:text-base">
                            Los Angeles, CA 90024
                          </p>
                          <Badge className="bg-emerald-100 text-emerald-800 mt-3">
                            Default
                          </Badge>
                        </div>
                        <Button
                          variant="outline"
                          className="w-full sm:w-auto transition-all duration-300 hover:scale-105"
                        >
                          Edit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Settings */}
              {activeTab === "settings" && (
                <div className="space-y-4 sm:space-y-6">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 font-display">
                    Account Settings
                  </h2>

                  <Card className="animate-zoom-in">
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg">
                        Profile Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="text-sm font-medium text-gray-700">
                            Full Name
                          </label>
                          <p className="text-base sm:text-lg text-gray-900 mt-1">
                            {user.name}
                          </p>
                        </div>
                        {user.role == "Guest" && (
                          <div>
                            <label className="text-sm font-medium text-gray-700">
                              Email
                            </label>
                            <p className="text-base sm:text-lg text-gray-900 mt-1">
                              {user.email}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card
                    className="animate-zoom-in"
                    style={{ animationDelay: "0.1s" }}
                  >
                    <CardHeader>
                      <CardTitle className="text-base sm:text-lg">
                        Contact Information
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center space-x-3 text-gray-600">
                        <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base">
                          Support:{" "}
                          {formatCanadianPhoneNumber(restaurant?.phone || "")}
                        </span>
                      </div>
                      <div className="flex items-center space-x-3 text-gray-600">
                        <Mail className="w-4 h-4 sm:w-5 sm:h-5" />
                        <span className="text-sm sm:text-base">
                          Email: {restaurant?.email}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          </div>

          {/* Order Details Modal */}
          <Dialog
            open={isOrderDetailsOpen}
            onOpenChange={setIsOrderDetailsOpen}
          >
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto animate-zoom-in">
              <DialogHeader>
                <DialogTitle className="text-lg sm:text-xl font-bold">
                  Order Details - #{selectedOrder?.orderId}
                </DialogTitle>
              </DialogHeader>

              {selectedOrder && (
                <div className="space-y-4 sm:space-y-6">
                  {/* Order Status */}
                  <div className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg">
                    <div>
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                        Order Status
                      </h3>
                      <p className="text-xs sm:text-sm text-gray-600">
                        Placed on{" "}
                        {selectedOrder?.createdAt &&
                          new Date(selectedOrder.createdAt).toLocaleString(
                            "en-US",
                            {
                              month: "short", // Aug
                              day: "numeric", // 30
                              year: "numeric", // 2025
                              hour: "numeric", // 11
                              minute: "2-digit", // 57
                              hour12: true, // AM/PM
                            }
                          )}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Badge className={getStatusColor(selectedOrder.status)}>
                        {getStatusText(selectedOrder.status)}
                      </Badge>
                      {selectedOrder.hasRating && (
                        <Badge className="bg-yellow-100 text-yellow-800">
                          <Star className="w-3 h-3 mr-1 fill-current" />
                          Rated
                        </Badge>
                      )}
                    </div>
                  </div>

                  {/* Restaurant & Delivery Info */}
                  <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                    {selectedOrder.orderMode == "pickup" && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm sm:text-base">
                            Pickup Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="font-medium text-gray-900 text-sm sm:text-base">
                            {selectedOrder.restaurant}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {restaurant?.address1}
                          </p>
                          <p className="text-xs sm:text-sm text-gray-600">
                            {restaurant?.address2}
                          </p>
                        </CardContent>
                      </Card>
                    )}

                    {selectedOrder.orderMode == "delivery" && (
                      <Card>
                        <CardHeader className="pb-3">
                          <CardTitle className="text-sm sm:text-base">
                            Delivery Details
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-xs sm:text-sm text-gray-600 mb-1">
                            Delivered to:
                          </p>
                          <p className="font-medium text-gray-900 text-sm sm:text-base">
                            {selectedOrder.shippingAddress?.addressLine1}
                          </p>
                          {selectedOrder?.deliveryTime && (
                            <p className="text-xs sm:text-sm text-gray-600 mt-2">
                              Delivered at {selectedOrder?.deliveryTime}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>

                  {/* Order Items */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm sm:text-base">
                        Order Items
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {selectedOrder.items.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center justify-between py-2"
                          >
                            <div>
                              <span className="font-medium text-gray-900 text-sm sm:text-base">
                                {item.name}
                              </span>
                              <span className="text-gray-600 ml-2 text-sm">
                                x{item.quantity}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900 text-sm sm:text-base">
                              ${(item.price * item.quantity).toFixed(2)}
                            </span>
                          </div>
                        ))}

                        <Separator className="my-3" />

                        <div className="space-y-2">
                          <div className="flex justify-between text-xs sm:text-sm">
                            <span>Subtotal</span>
                            <span>
                              $ {selectedOrder?.totalAmount?.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs sm:text-sm">
                            <span>Delivery Fee</span>
                            <span>
                              $ {selectedOrder?.deliveryFee?.toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm items-center">
                            <div className="flex items-center gap-1 relative">
                              <span>Taxes and Service fee</span>
                              <button
                                type="button"
                                onClick={() => setOpenTaxToolTip(!openTaxToolTip)}
                                onMouseEnter={() => setOpenTaxToolTip(true)}
                                onMouseLeave={() => setOpenTaxToolTip(false)}
                                className="text-gray-500 hover:text-gray-700"
                              >
                                <Info size={18} />
                              </button>
                              {/* Tooltip */}
                              {openTaxToolTip && (
                                <div className="absolute top-6 left-0 z-50 w-52 rounded-md border bg-white p-3 shadow-lg text-xs">
                                  {selectedOrder?.orderTaxes?.map(
                                    (tax: any) => (
                                      <div
                                        key={tax.name}
                                        className="flex justify-between"
                                      >
                                        <span>
                                          {tax.name} ({tax.percentage}%)
                                        </span>
                                        <span>${tax.amount}</span>
                                      </div>
                                    )
                                  )}
                                  <div className="flex justify-between">
                                    <span>Service fee</span>
                                    <span>
                                      ${restaurant?.serviceFee?.toFixed(2)}
                                    </span>
                                  </div>
                                </div>
                              )}
                            </div>
                            {selectedOrder && (
                              <span>
                                $
                                {(
                                  selectedOrder?.taxAmount +
                                  selectedOrder?.serviceFee
                                )?.toFixed(2)}
                              </span>
                            )}
                          </div>
                          {selectedOrder.tipAmount && (
                            <div className="flex justify-between text-xs sm:text-sm">
                              <span>Tip</span>
                              <span>
                                ${selectedOrder?.tipAmount?.toFixed(2)}
                              </span>
                            </div>
                          )}

                          <Separator />
                          <div className="flex justify-between font-bold text-base sm:text-lg">
                            <span>Total</span>
                            <span className="text-emerald-600">
                              $
                              {(
                                selectedOrder?.totalAmount +
                                selectedOrder?.deliveryFee
                              )?.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                    <Button
                      variant="outline"
                      className="flex-1 transition-all duration-300 hover:scale-105"
                      onClick={() => repeatOrder(selectedOrder)}
                    >
                      <Repeat className="w-4 h-4 mr-2" />
                      Reorder
                    </Button>
                    <Button
                      variant="outline"
                      className="flex-1 transition-all duration-300 hover:scale-105"
                    >
                      <Phone className="w-4 h-4 mr-2" />
                      Contact Support
                    </Button>
                    {selectedOrder.status === "delivered" &&
                      !selectedOrder.hasRating && (
                        <Button
                          className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-white transition-all duration-300 hover:scale-105"
                          onClick={() => {
                            setIsOrderDetailsOpen(false);
                            handleRateOrder(selectedOrder.orderId);
                          }}
                        >
                          <Star className="w-4 h-4 mr-2" />
                          Rate Order
                        </Button>
                      )}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Simple Rating Modal */}
          <SimpleRatingModal
            isOpen={isRatingModalOpen}
            onClose={() => setIsRatingModalOpen(false)}
            orderId={orderToRate}
            onSubmitRating={handleRatingSubmit}
          />
        </div>
      </div>
      <Footer />
    </>
  );
}
